import { useEffect, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useCompany } from '../../hooks/useCompany'
import { supabase } from '../../services/supabase'
import { SUPPLIER_RISK_ALERT_DOC_UPLOAD_CONFIG } from '../../utils/constants'
import { CATEGORIES, subcategoriesFor } from './riskIntakeTaxonomy'
import { emptySupplierDraft, getCompanyLogoUrl } from './supplierRiskAlertShared'

const field =
  'w-full rounded-lg border border-es-hairline bg-es-white px-3 py-2 text-sm text-es-navy focus:outline-none focus:ring-2 focus:ring-es-signal/40'

type CompanyOption = { id: string; name: string; domain?: string | null }
type PriceRow = { productCategory: string; tradeNames: string; increase: string }
type SupplierDraft = ReturnType<typeof emptySupplierDraft>

const emptyPrice = (): PriceRow => ({ productCategory: '', tradeNames: '', increase: '' })
const MAX_SUPPLIERS = 10
const MAX_NEXT_BEST_ACTIONS = 3

function truncateBulletLines(text: string, max: number): string {
  return text
    .split(/\n+/)
    .map((l) => l.replace(/^[-•*]\s*/, '').trim())
    .filter(Boolean)
    .slice(0, max)
    .join('\n')
}

function sanitizeFileName(name: string): string {
  const base = name.replace(/^.*[/\\]/, '').trim()
  const safe = base.replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 120)
  return safe || 'document'
}

function validateAttachment(file: File): string | null {
  const allowed = SUPPLIER_RISK_ALERT_DOC_UPLOAD_CONFIG.ALLOWED_TYPES as readonly string[]
  const byExt = /\.(pdf|docx?)$/i.test(file.name)
  if (!allowed.includes(file.type) && !byExt) {
    return 'Invalid file type. Allowed: PDF or Word (.doc/.docx).'
  }
  if (file.size > SUPPLIER_RISK_ALERT_DOC_UPLOAD_CONFIG.MAX_FILE_SIZE) {
    return `File too large. Maximum size is ${SUPPLIER_RISK_ALERT_DOC_UPLOAD_CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB.`
  }
  return null
}

function guessContentType(file: File): string {
  if (file.type) return file.type
  if (/\.pdf$/i.test(file.name)) return 'application/pdf'
  if (/\.docx$/i.test(file.name)) {
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  }
  if (/\.doc$/i.test(file.name)) return 'application/msword'
  return 'application/octet-stream'
}

/** Admin-only: send Day 1 Supplier risk alert (Stepan Word layout) */
export function SendSupplierRiskAlertView() {
  const { isAdmin, viewAsCompanyId, effectiveCompanyId, user } = useAuth()
  const { company } = useCompany()

  const [companies, setCompanies] = useState<CompanyOption[]>([])
  const [targetCompanyId, setTargetCompanyId] = useState('')
  const [targetLogoUrl, setTargetLogoUrl] = useState<string | null>(null)
  const [targetName, setTargetName] = useState('')

  const [cmEmails, setCmEmails] = useState('')
  const [suppliers, setSuppliers] = useState<SupplierDraft[]>([emptySupplierDraft()])
  const [feedbackMode, setFeedbackMode] = useState<'event' | 'per_supplier'>('event')
  const [issueSummary, setIssueSummary] = useState('')
  const [announcedDate, setAnnouncedDate] = useState('')
  const [severity, setSeverity] = useState('HIGH')
  const [location, setLocation] = useState('')
  const [region, setRegion] = useState('')
  const [immediateAction, setImmediateAction] = useState('')
  const [eventOverview, setEventOverview] = useState('')
  const [priceRows, setPriceRows] = useState<PriceRow[]>([emptyPrice()])
  const [whyItMatters, setWhyItMatters] = useState('')
  const [nextBestAction, setNextBestAction] = useState('')
  const [keyTakeaway, setKeyTakeaway] = useState('')
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<{
    id: string
    feedbackUrl?: string
    companyName?: string
    attachmentAttached?: boolean
    supplierCount?: number
    feedbackMode?: string
  } | null>(null)

  useEffect(() => {
    if (!isAdmin) return
    ;(async () => {
      const { data } = await supabase.from('companies').select('id, name, domain').order('name')
      setCompanies((data as CompanyOption[]) || [])
    })()
  }, [isAdmin])

  useEffect(() => {
    const pref = viewAsCompanyId || effectiveCompanyId || company?.id || ''
    if (pref && !targetCompanyId) setTargetCompanyId(pref)
  }, [viewAsCompanyId, effectiveCompanyId, company?.id, targetCompanyId])

  useEffect(() => {
    if (!targetCompanyId) {
      setTargetLogoUrl(null)
      setTargetName('')
      return
    }
    const opt = companies.find((c) => c.id === targetCompanyId)
    setTargetName(opt?.name || '')
    ;(async () => {
      const { data } = await supabase
        .from('company_settings')
        .select('theme_config')
        .eq('company_id', targetCompanyId)
        .maybeSingle()
      setTargetLogoUrl(getCompanyLogoUrl((data?.theme_config as any)?.logo))
    })()
  }, [targetCompanyId, companies])

  if (!isAdmin) {
    return <p className="text-sm text-es-muted">Only The Simple admins can send risk alerts.</p>
  }

  function updateSupplier(idx: number, patch: Partial<SupplierDraft>) {
    setSuppliers((prev) => {
      const next = [...prev]
      next[idx] = { ...next[idx], ...patch }
      return next
    })
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setResult(null)
    if (!targetCompanyId) {
      setError('Select the target company before sending.')
      return
    }
    const cleaned = suppliers
      .map((s) => ({
        ...s,
        supplierName: s.supplierName.trim(),
      }))
      .filter((s) => s.supplierName)
    if (!cleaned.length) {
      setError('Add at least one supplier name.')
      return
    }
    if (cleaned.length > MAX_SUPPLIERS) {
      setError(`At most ${MAX_SUPPLIERS} suppliers per event.`)
      return
    }
    if (attachmentFile) {
      const verr = validateAttachment(attachmentFile)
      if (verr) {
        setError(verr)
        return
      }
    }
    setBusy(true)
    try {
      let attachmentPath: string | undefined
      let attachmentName: string | undefined
      let attachmentContentType: string | undefined

      if (attachmentFile) {
        const safeName = sanitizeFileName(attachmentFile.name)
        const unique = `${Date.now()}-${Math.random().toString(36).substring(2, 10)}`
        attachmentPath = `${targetCompanyId}/${unique}_${safeName}`
        attachmentName = attachmentFile.name
        attachmentContentType = guessContentType(attachmentFile)

        const { error: uploadError } = await supabase.storage
          .from(SUPPLIER_RISK_ALERT_DOC_UPLOAD_CONFIG.STORAGE_BUCKET)
          .upload(attachmentPath, attachmentFile, {
            cacheControl: '3600',
            upsert: false,
            contentType: attachmentContentType,
          })
        if (uploadError) throw uploadError
      }

      const priceIncreases = priceRows.filter((r) => r.productCategory || r.tradeNames || r.increase)
      const nextBestTrimmed = truncateBulletLines(nextBestAction, MAX_NEXT_BEST_ACTIONS)
      const supplierPayload = cleaned.map((s) => ({
        supplierName: s.supplierName,
        uniqueId: s.uniqueId?.trim() || undefined,
        category: s.category?.trim() || undefined,
        subCategory: s.subCategory?.trim() || undefined,
        plant: s.plant?.trim() || undefined,
        brand: s.brand?.trim() || undefined,
        singleSource: s.singleSource === 'Yes',
      }))
      const primary = supplierPayload[0]

      const { data, error: fnErr } = await supabase.functions.invoke('send-supplier-risk-alert', {
        body: {
          companyId: targetCompanyId,
          categoryManagerEmails: cmEmails,
          feedbackMode,
          suppliers: supplierPayload,
          ...(attachmentPath
            ? { attachmentPath, attachmentName, attachmentContentType }
            : {}),
          alertPayload: {
            supplierName: primary.supplierName,
            uniqueId: primary.uniqueId,
            category: primary.category,
            suppliers: supplierPayload,
            feedbackMode,
            issueSummary,
            announcedDate,
            severity,
            location,
            region,
            immediateAction,
            eventOverview,
            whyItMatters,
            nextBestAction: nextBestTrimmed,
            keyTakeaway,
            priceIncreases,
          },
        },
      })
      if (fnErr) throw fnErr
      if (data?.error) throw new Error(data.error)
      setResult({
        id: data.id,
        feedbackUrl: data.feedbackUrl,
        companyName: data.companyName,
        attachmentAttached: data.attachmentAttached,
        supplierCount: data.supplierCount,
        feedbackMode: data.feedbackMode,
      })
      setAttachmentFile(null)
    } catch (err: any) {
      setError(err.message || 'Send failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {targetLogoUrl && (
          <img src={targetLogoUrl} alt="" className="h-10 max-w-[140px] object-contain" />
        )}
        <div>
          <h1 className="font-esDisplay text-2xl text-es-navy">Send supplier risk alert</h1>
          <p className="text-sm text-es-muted mt-1">
            One Day 1 email for the event (lists all suppliers). Feedback reminders on Day 3 and Day 5.
            Signed in as <strong>{user?.email}</strong>.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm">{error}</div>
      )}
      {result && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-3 text-sm space-y-1">
          <p>
            Event sent to <strong>{result.companyName || targetName}</strong> with{' '}
            <strong>{result.supplierCount ?? 1}</strong> supplier
            {(result.supplierCount ?? 1) === 1 ? '' : 's'} (
            {result.feedbackMode === 'per_supplier' ? 'per-supplier answers' : 'one event answer'}; id{' '}
            {result.id}).
          </p>
          {result.attachmentAttached && (
            <p className="text-xs">Original Word/PDF was attached to the email.</p>
          )}
          {result.feedbackUrl && (
            <p className="text-xs break-all">Feedback link: {result.feedbackUrl}</p>
          )}
        </div>
      )}

      <form onSubmit={onSubmit} className="rounded-lg border border-es-hairline bg-es-white p-5 space-y-4">
        <label className="block text-sm">
          Target company *
          <select
            className={`${field} mt-1`}
            required
            value={targetCompanyId}
            onChange={(e) => setTargetCompanyId(e.target.value)}
          >
            <option value="">Select company…</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.domain ? ` (${c.domain})` : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          Category manager emails * (comma-separated)
          <input
            className={`${field} mt-1`}
            required
            value={cmEmails}
            onChange={(e) => setCmEmails(e.target.value)}
          />
        </label>

        <label className="block text-sm">
          Attach Word/PDF (exact client document)
          <input
            className={`${field} mt-1`}
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
          />
          <span className="mt-1 block text-xs text-es-muted">
            Optional. Attached to the Day 1 email and available for download in Risk alerts. Max{' '}
            {SUPPLIER_RISK_ALERT_DOC_UPLOAD_CONFIG.MAX_FILE_SIZE / (1024 * 1024)}MB.
          </span>
          {attachmentFile && (
            <span className="mt-1 block text-xs text-es-navy">Selected: {attachmentFile.name}</span>
          )}
        </label>

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-es-navy">Impact assessment type</legend>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="feedbackMode"
              checked={feedbackMode === 'event'}
              onChange={() => setFeedbackMode('event')}
            />
            <span>
              <span className="font-medium">One response for the whole event</span>
              <span className="block text-xs text-es-muted">
                Category managers answer the four questions once for all listed suppliers.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="feedbackMode"
              checked={feedbackMode === 'per_supplier'}
              onChange={() => setFeedbackMode('per_supplier')}
            />
            <span>
              <span className="font-medium">Separate response per supplier</span>
              <span className="block text-xs text-es-muted">
                They can submit suppliers as they go; the event stays open until all are answered.
              </span>
            </span>
          </label>
        </fieldset>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-es-navy">Suppliers in this event *</p>
            <button
              type="button"
              className="text-xs font-semibold text-es-signal disabled:opacity-40"
              disabled={suppliers.length >= MAX_SUPPLIERS}
              onClick={() => setSuppliers((s) => [...s, emptySupplierDraft()])}
            >
              + Add supplier
            </button>
          </div>
          <p className="text-xs text-es-muted">
            One email lists all suppliers (up to {MAX_SUPPLIERS}). One feedback link for the event.
          </p>
          {suppliers.map((s, idx) => (
            <div key={idx} className="rounded-lg border border-es-hairline bg-es-haze/40 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-es-muted">
                  Supplier {idx + 1}
                </p>
                {suppliers.length > 1 && (
                  <button
                    type="button"
                    className="text-xs font-semibold text-red-700"
                    onClick={() => setSuppliers((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    Remove
                  </button>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <label className="block text-sm sm:col-span-2">
                  Name *
                  <input
                    className={`${field} mt-1`}
                    required
                    value={s.supplierName}
                    onChange={(e) => updateSupplier(idx, { supplierName: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  Unique ID
                  <input
                    className={`${field} mt-1`}
                    value={s.uniqueId || ''}
                    onChange={(e) => updateSupplier(idx, { uniqueId: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  Category
                  <select
                    className={`${field} mt-1`}
                    value={s.category || ''}
                    onChange={(e) =>
                      updateSupplier(idx, { category: e.target.value, subCategory: '' })
                    }
                  >
                    <option value="">Select category</option>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  Sub-category
                  <select
                    className={`${field} mt-1`}
                    value={s.subCategory || ''}
                    onChange={(e) => updateSupplier(idx, { subCategory: e.target.value })}
                    disabled={!s.category}
                  >
                    <option value="">Select sub-category</option>
                    {subcategoriesFor(s.category || '').map((sc) => (
                      <option key={sc} value={sc}>
                        {sc}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  Plant
                  <input
                    className={`${field} mt-1`}
                    value={s.plant || ''}
                    onChange={(e) => updateSupplier(idx, { plant: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  Brand
                  <input
                    className={`${field} mt-1`}
                    value={s.brand || ''}
                    onChange={(e) => updateSupplier(idx, { brand: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  Single source
                  <select
                    className={`${field} mt-1`}
                    value={s.singleSource}
                    onChange={(e) => updateSupplier(idx, { singleSource: e.target.value })}
                  >
                    <option>Yes</option>
                    <option>No</option>
                  </select>
                </label>
              </div>
            </div>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="block text-sm sm:col-span-2">
            Issue summary (alert description)
            <input
              className={`${field} mt-1`}
              value={issueSummary}
              onChange={(e) => setIssueSummary(e.target.value)}
              placeholder="e.g. Price increases across selected surfactant categories"
            />
            <span className="mt-1 block text-xs text-es-muted">
              Do not repeat “Single Source” here — it is added automatically when all suppliers are
              single-source.
            </span>
          </label>
          <label className="block text-sm">
            Announced date
            <input
              className={`${field} mt-1`}
              value={announcedDate}
              onChange={(e) => setAnnouncedDate(e.target.value)}
              placeholder="1 Jul 2026"
            />
          </label>
          <label className="block text-sm">
            Severity
            <select className={`${field} mt-1`} value={severity} onChange={(e) => setSeverity(e.target.value)}>
              <option>HIGH</option>
              <option>MEDIUM</option>
              <option>LOW</option>
            </select>
          </label>
          <label className="block text-sm">
            Location
            <input className={`${field} mt-1`} value={location} onChange={(e) => setLocation(e.target.value)} />
          </label>
          <label className="block text-sm">
            Region
            <input className={`${field} mt-1`} value={region} onChange={(e) => setRegion(e.target.value)} />
          </label>
        </div>

        <label className="block text-sm">
          Immediate action required
          <textarea
            className={`${field} mt-1`}
            rows={2}
            value={immediateAction}
            onChange={(e) => setImmediateAction(e.target.value)}
          />
        </label>

        <label className="block text-sm">
          Event overview (one bullet per line)
          <textarea
            className={`${field} mt-1`}
            rows={4}
            value={eventOverview}
            onChange={(e) => setEventOverview(e.target.value)}
          />
        </label>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-es-navy">Price increase rows (optional)</p>
            <button
              type="button"
              className="text-xs font-semibold text-es-signal"
              onClick={() => setPriceRows((r) => [...r, emptyPrice()])}
            >
              + Add row
            </button>
          </div>
          {priceRows.map((row, idx) => (
            <div key={idx} className="grid sm:grid-cols-3 gap-2">
              <input
                className={field}
                placeholder="Product category"
                value={row.productCategory}
                onChange={(e) => {
                  const next = [...priceRows]
                  next[idx] = { ...next[idx], productCategory: e.target.value }
                  setPriceRows(next)
                }}
              />
              <input
                className={field}
                placeholder="Trade name(s)"
                value={row.tradeNames}
                onChange={(e) => {
                  const next = [...priceRows]
                  next[idx] = { ...next[idx], tradeNames: e.target.value }
                  setPriceRows(next)
                }}
              />
              <input
                className={field}
                placeholder="Increase"
                value={row.increase}
                onChange={(e) => {
                  const next = [...priceRows]
                  next[idx] = { ...next[idx], increase: e.target.value }
                  setPriceRows(next)
                }}
              />
            </div>
          ))}
        </div>

        <label className="block text-sm">
          Why it matters (one bullet per line)
          <textarea
            className={`${field} mt-1`}
            rows={3}
            value={whyItMatters}
            onChange={(e) => setWhyItMatters(e.target.value)}
          />
        </label>
        <label className="block text-sm">
          Next best actions (max {MAX_NEXT_BEST_ACTIONS}, one per line)
          <textarea
            className={`${field} mt-1`}
            rows={3}
            value={nextBestAction}
            onChange={(e) => setNextBestAction(e.target.value)}
          />
          <span className="mt-1 block text-xs text-es-muted">
            Only the first {MAX_NEXT_BEST_ACTIONS} bullets are included in the email.
          </span>
        </label>
        <label className="block text-sm">
          Key takeaway
          <textarea
            className={`${field} mt-1`}
            rows={2}
            value={keyTakeaway}
            onChange={(e) => setKeyTakeaway(e.target.value)}
          />
          <span className="mt-1 block text-xs text-es-muted">
            Keep tight to suggested actions — no product pitch.
          </span>
        </label>

        <button
          type="submit"
          disabled={busy}
          className="rounded-full bg-es-signal text-es-white px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
        >
          {busy ? 'Sending…' : 'Send Day 1 alert'}
        </button>
      </form>
    </div>
  )
}
